import React, { useCallback, useState } from "react";
import { X, Plus, Trash2, MessageSquare, ChevronRight, Clock, SlidersHorizontal, Palette } from "lucide-react";
import { useSessionStore } from "../store/sessionStore";
import { useSettingsStore, type ThemeId } from "../store/settingsStore";
import type { AppAdapter } from "../types";

interface SettingsPanelProps {
  adapter: AppAdapter;
  onClose: () => void;
  onClearMessages: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onLoadMessages: (messages: any[]) => void;
}

type Tab = "history" | "settings";

const THEMES: { id: ThemeId; label: string; colors: [string, string, string] }[] = [
  { id: "dark-neon", label: "Dark Neon", colors: ["#A78BFA", "#F472B6", "#0C0D12"] },
  { id: "midnight", label: "Midnight", colors: ["#60A5FA", "#818CF8", "#0A1628"] },
  { id: "light", label: "Light", colors: ["#7C3AED", "#DB2777", "#F5F6FA"] },
];

export function SettingsPanel({ adapter, onClose, onClearMessages, onLoadMessages }: SettingsPanelProps) {
  const [tab, setTab] = useState<Tab>("history");
  const { modelId, setModelId, maxSteps, setMaxSteps, theme, setTheme } = useSettingsStore();
  const {
    sessions,
    activeSessionId,
    createSession,
    switchSession,
    deleteSession,
  } = useSessionStore();

  const handleNewSession = useCallback(async () => {
    const docId = adapter.getContextId
      ? await adapter.getContextId()
      : "default";
    const session = createSession(docId);
    onClearMessages();
    return session;
  }, [adapter, createSession, onClearMessages]);

  const handleSwitchSession = useCallback(
    async (sessionId: string) => {
      await switchSession(sessionId);
      const session = useSessionStore.getState().sessions.find((s) => s.id === sessionId);
      onLoadMessages(session?.messages ?? []);
      onClose();
    },
    [switchSession, onLoadMessages, onClose]
  );

  const handleDeleteSession = useCallback(
    (sessionId: string) => {
      deleteSession(sessionId);
      if (sessionId === activeSessionId) {
        onClearMessages();
      }
    },
    [deleteSession, activeSessionId, onClearMessages]
  );

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  return (
    <div className="absolute inset-0 z-30 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative ml-auto w-full max-w-[320px] h-full bg-surface border-l border-border-subtle flex flex-col cai-sidebar-enter shadow-[-8px_0_40px_rgba(0,0,0,0.6)]">
        {/* Panel header */}
        <div className="px-4 py-3 flex items-center justify-between border-b border-border-subtle shrink-0">
          <div className="flex items-center gap-1 bg-surface-raised rounded-lg p-0.5">
            <button
              onClick={() => setTab("history")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${
                tab === "history"
                  ? "bg-accent-violet/15 text-accent-violet"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              <Clock size={12} />
              History
            </button>
            <button
              onClick={() => setTab("settings")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${
                tab === "settings"
                  ? "bg-accent-violet/15 text-accent-violet"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              <SlidersHorizontal size={12} />
              Settings
            </button>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-surface-raised transition-colors"
            aria-label="Close panel"
          >
            <X size={14} />
          </button>
        </div>

        {/* Panel content */}
        <div className="flex-1 overflow-y-auto cai-scrollbar">
          {tab === "history" ? (
            <div className="p-3">
              <button
                onClick={handleNewSession}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 mb-3 rounded-xl text-[12px] font-semibold text-white cai-glow-btn"
              >
                <Plus size={13} strokeWidth={2.5} />
                New Chat
              </button>

              {sessions.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-10 h-10 bg-surface-raised rounded-full flex items-center justify-center mx-auto mb-3">
                    <MessageSquare size={18} className="text-text-muted" />
                  </div>
                  <p className="text-[12px] text-text-muted">No conversations yet</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {[...sessions]
                    .sort((a, b) => b.updatedAt - a.updatedAt)
                    .map((session) => (
                      <div
                        key={session.id}
                        className={`group flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all ${
                          session.id === activeSessionId
                            ? "bg-accent-violet/10 border border-accent-violet/20"
                            : "hover:bg-surface-raised border border-transparent"
                        }`}
                        onClick={() => handleSwitchSession(session.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSwitchSession(session.id);
                          }
                        }}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          session.id === activeSessionId
                            ? "bg-accent-violet/15 text-accent-violet"
                            : "bg-surface-raised text-text-muted"
                        }`}>
                          <MessageSquare size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-medium text-text-primary truncate leading-tight">
                            {session.name}
                          </div>
                          <div className="text-[10px] text-text-secondary mt-0.5">
                            {formatTime(session.updatedAt)}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSession(session.id);
                          }}
                          className="text-text-muted hover:text-ca-punch transition-colors shrink-0 opacity-0 group-hover:opacity-100"
                          aria-label={`Delete session ${session.name}`}
                        >
                          <Trash2 size={13} />
                        </button>
                        <ChevronRight size={12} className="text-text-muted shrink-0 opacity-0 group-hover:opacity-100" />
                      </div>
                    ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {/* Theme Selection */}
              <div>
                <div className="flex items-center gap-1.5 mb-3">
                  <Palette size={12} className="text-accent-violet" />
                  <label className="text-[10px] font-semibold text-text-secondary uppercase tracking-wider">
                    Theme
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {THEMES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTheme(t.id)}
                      className={`relative flex flex-col items-center gap-1.5 p-2.5 rounded-xl border transition-all ${
                        theme === t.id
                          ? "border-accent-violet bg-accent-violet/10"
                          : "border-border-subtle hover:border-accent-violet/30 bg-surface-raised"
                      }`}
                    >
                      <div className="flex gap-0.5">
                        {t.colors.map((c, i) => (
                          <div
                            key={i}
                            className="w-4 h-4 rounded-full"
                            style={{ background: c, boxShadow: `0 0 6px ${c}40` }}
                          />
                        ))}
                      </div>
                      <span className="text-[10px] font-medium text-text-primary">{t.label}</span>
                      {theme === t.id && (
                        <div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-accent-violet" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Model Selection */}
              <div>
                <label
                  htmlFor="model-select"
                  className="block text-[10px] font-semibold text-text-secondary uppercase tracking-wider mb-2"
                >
                  Model
                </label>
                <select
                  id="model-select"
                  value={modelId}
                  onChange={(e) => setModelId(e.target.value)}
                  className="w-full bg-surface-raised border border-border-subtle rounded-xl px-3 py-2.5 text-[12px] text-text-primary focus:outline-none focus:border-accent-violet/50 focus:shadow-[0_0_0_3px_rgba(167,139,250,0.1)] transition-all appearance-none cursor-pointer"
                >
                  <option value="gpt-5.4">GPT-5.4</option>
                  <option value="KIMI_K2_THINKING">Kimi K2</option>
                  <option value="global.anthropic.claude-sonnet-4-6">Claude Sonnet 4.6</option>
                </select>
              </div>

              {/* Max Steps */}
              <div>
                <label
                  htmlFor="max-steps"
                  className="block text-[10px] font-semibold text-text-secondary uppercase tracking-wider mb-2"
                >
                  Max Tool Steps
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="max-steps"
                    type="range"
                    min={1}
                    max={20}
                    value={maxSteps}
                    onChange={(e) => setMaxSteps(Number(e.target.value))}
                    className="flex-1 accent-[var(--color-accent-violet)] h-1.5"
                  />
                  <span className="text-[12px] font-mono text-text-primary bg-surface-raised border border-border-subtle rounded-lg px-2.5 py-1 min-w-[36px] text-center">
                    {maxSteps}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
